import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  Snackbar,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import ClearIcon from "@mui/icons-material/Clear";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/AuthContext";
import dayjs from "dayjs";
import {
  getCampaignProductSyncStatus,
  getCachedCampaignProductsAdmin,
  syncCampaignProducts,
  getCampaignProductSyncJobStatus,
  type CampaignProductSyncStatus,
  type CampaignProduct,
} from "api/order-entry.api";

const SYNC_POLL_INTERVAL_MS = 2000;

export default function CampaignProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const queryClient = useQueryClient();

  const [selectedCampaign, setSelectedCampaign] = useState<CampaignProductSyncStatus | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [sortField, setSortField] = useState<"productName" | "productSku" | "price" | "shippingPrice">("productName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data, isLoading: campaignsLoading } = useQuery({
    queryKey: ["campaign-product-sync-status"],
    queryFn: () => getCampaignProductSyncStatus().then((r) => r.data),
    enabled: isAdmin,
  });

  const allCampaigns: CampaignProductSyncStatus[] = useMemo(
    () => data?.campaigns ?? [],
    [data]
  );
  const syncedCount = allCampaigns.filter((c) => c.syncedAt).length;

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ["campaign-products-admin", selectedCampaign?.campaignId],
    queryFn: () =>
      getCachedCampaignProductsAdmin(selectedCampaign!.campaignId).then((r) => r.data),
    enabled: selectedCampaign !== null,
  });

  const [syncingCampaignId, setSyncingCampaignId] = useState<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Sync runs in the background on the server (large campaigns can take
  // minutes — longer than a proxy/CDN would hold a request open, which
  // showed up as a browser-reported CORS error). Start it, then poll status
  // instead of waiting on the POST response.
  const pollSyncStatus = useCallback(
    (campaignId: number) => {
      getCampaignProductSyncJobStatus(campaignId)
        .then((res) => {
          const job = res.data.job;
          if (!job || job.status === "running") {
            pollTimerRef.current = setTimeout(
              () => pollSyncStatus(campaignId),
              SYNC_POLL_INTERVAL_MS
            );
            return;
          }

          setSyncingCampaignId(null);
          queryClient.invalidateQueries({ queryKey: ["campaign-product-sync-status"] });
          queryClient.invalidateQueries({ queryKey: ["campaign-products-admin"] });

          if (job.status === "succeeded") {
            setSnackbar({
              open: true,
              message: job.result
                ? `Synced ${job.result.productsCount} product${job.result.productsCount === 1 ? "" : "s"}`
                : "Products synced",
              severity: "success",
            });
          } else {
            setSnackbar({
              open: true,
              message: job.error || "Failed to sync products",
              severity: "error",
            });
          }
        })
        .catch(() => {
          // Transient poll failure — keep polling rather than giving up.
          pollTimerRef.current = setTimeout(
            () => pollSyncStatus(campaignId),
            SYNC_POLL_INTERVAL_MS
          );
        });
    },
    [queryClient]
  );

  const handleSyncClick = async (campaignId: number) => {
    stopPolling();
    setSyncingCampaignId(campaignId);
    try {
      await syncCampaignProducts(campaignId);
      pollSyncStatus(campaignId);
    } catch (err: any) {
      setSyncingCampaignId(null);
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to start sync",
        severity: "error",
      });
    }
  };

  useEffect(() => stopPolling, [stopPolling]);

  if (!isAdmin) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Typography
              component="span"
              sx={{ cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/")}
            >
              Go back
            </Typography>
          }
        >
          You don't have access to this page.
        </Alert>
      </Box>
    );
  }

  const allProducts: CampaignProduct[] = productsData?.products ?? [];
  // Look up the live sync status by id instead of using the stale selected
  // option object, so "Last synced" updates immediately after a sync.
  const selectedCampaignLive = selectedCampaign
    ? allCampaigns.find((c) => c.campaignId === selectedCampaign.campaignId) ?? selectedCampaign
    : null;

  const products = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    let filtered = allProducts;
    if (q) {
      filtered = allProducts.filter((p: CampaignProduct) => {
        const name = (p.productName || "").toLowerCase();
        const sku = ((p as any).productSku || "").toLowerCase();
        return name.includes(q) || sku.includes(q) || String(p.campaignProductId).includes(q);
      });
    }

    const sorted = [...filtered].sort((a: CampaignProduct, b: CampaignProduct) => {
      let cmp: number;
      if (sortField === "productName") {
        cmp = (a.productName || "").localeCompare(b.productName || "");
      } else if (sortField === "productSku") {
        cmp = ((a as any).productSku || "").localeCompare((b as any).productSku || "");
      } else if (sortField === "price") {
        cmp = (parseFloat(a.price || "0") || 0) - (parseFloat(b.price || "0") || 0);
      } else {
        cmp = (parseFloat(a.shippingPrice || "0") || 0) - (parseFloat(b.shippingPrice || "0") || 0);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [allProducts, productSearch, sortField, sortDir]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>
            Campaign Products
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Order Entry's product dropdown reads only from this cache — fetching a
            campaign's products live from CheckoutChamp can take minutes for large
            catalogs. Select a campaign below and sync it (one at a time) to make it
            fast, and re-sync whenever prices change. Order Entry itself will show a
            stale price otherwise, so re-sync before agents rely on the price shown here.
          </Typography>
        </Box>
        <Chip
          label={`${syncedCount} / ${allCampaigns.length} campaigns synced`}
          color={syncedCount === allCampaigns.length && allCampaigns.length > 0 ? "success" : "default"}
          variant="outlined"
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" mb={3} flexWrap="wrap">
        <Autocomplete
          options={allCampaigns}
          getOptionLabel={(opt) => opt.campaignName || `Campaign #${opt.campaignId}`}
          isOptionEqualToValue={(opt, val) => opt.campaignId === val.campaignId}
          loading={campaignsLoading}
          value={selectedCampaign}
          onChange={(_, val) => {
            setSelectedCampaign(val);
            setProductSearch("");
          }}
          sx={{ minWidth: 360 }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Campaign"
              placeholder="Select a campaign..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {campaignsLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.campaignId}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
                <Typography variant="body2">{option.campaignName || `Campaign #${option.campaignId}`}</Typography>
                {option.syncedAt ? (
                  <Chip label={`${option.productCount} synced`} size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                ) : (
                  <Chip label="Not synced" size="small" variant="outlined" sx={{ ml: 1, color: "text.secondary" }} />
                )}
              </Stack>
            </Box>
          )}
        />

        <Button
          variant="contained"
          startIcon={
            syncingCampaignId === selectedCampaign?.campaignId ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SyncIcon />
            )
          }
          onClick={() => selectedCampaign && handleSyncClick(selectedCampaign.campaignId)}
          disabled={!selectedCampaign || syncingCampaignId !== null}
        >
          {syncingCampaignId === selectedCampaign?.campaignId
            ? "Syncing... (this can take a few minutes for large campaigns)"
            : "Sync this campaign"}
        </Button>

        {selectedCampaignLive?.syncedAt && (
          <Typography variant="body2" color="text.secondary">
            Last synced {dayjs(selectedCampaignLive.syncedAt).format("MMM D, YYYY h:mm A")}
          </Typography>
        )}
      </Stack>

      {!selectedCampaign ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          Select a campaign above to view its cached products.
        </Typography>
      ) : productsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TextField
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            size="small"
            placeholder="Search products by name or SKU..."
            sx={{ mb: 2, minWidth: 320 }}
            InputProps={{
              endAdornment: productSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setProductSearch("")}>
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortField === "productName"}
                      direction={sortField === "productName" ? sortDir : "asc"}
                      onClick={() => handleSort("productName")}
                    >
                      Product
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortField === "productSku"}
                      direction={sortField === "productSku" ? sortDir : "asc"}
                      onClick={() => handleSort("productSku")}
                    >
                      SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortField === "price"}
                      direction={sortField === "price" ? sortDir : "asc"}
                      onClick={() => handleSort("price")}
                    >
                      Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortField === "shippingPrice"}
                      direction={sortField === "shippingPrice" ? sortDir : "asc"}
                      onClick={() => handleSort("shippingPrice")}
                    >
                      Shipping
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((p: CampaignProduct) => (
                  <TableRow key={p.campaignProductId}>
                    <TableCell>{p.productName || `Product #${p.campaignProductId}`}</TableCell>
                    <TableCell>{(p as any).productSku || "-"}</TableCell>
                    <TableCell align="right">${parseFloat(p.price || "0").toFixed(2)}</TableCell>
                    <TableCell align="right">${parseFloat(p.shippingPrice || "0").toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 3 }}>
                      {allProducts.length === 0
                        ? 'No cached products for this campaign — click "Sync this campaign" above.'
                        : "No products match your search."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
